import { Fragment, useContext, useEffect, useId, useState } from "react";
import { LayerInfo, MapInfo } from "src/common/managers/Types";
import { AppMessagesContext, Localization, SimpleTranslationTable, Utils, actionClearInfoMessage, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import { MitLatLng } from "src/managers/MapFacade";
import { Feature } from "src/states/ApplicationStateFeatures";
import { MapitStateActionType, MapitStateContext, actionUpdateDataLayer } from "src/states/MapitState";
import { ProjectContext, handleToBeAdded } from "src/states/ProjectState";
import { ProtectedFeature } from "./ProtectedFeature";
import { CatchmentInterface } from "src/managers/CatchmentInterface";
import { Button } from "react-bootstrap";
import { GenerateGeom, SpatialReturnType } from "src/managers/GenerateGeom";
import { PropertyInfoInterface } from "src/managers/PropertyInfoInterface";
import { Persistence } from "src/managers/Persistence";
import { CatchmentStateContext, actionGenerateCatchmentSimple } from "src/states/CatchmentState";
import { ReferenceGeomComposite } from "src/managers/ReferenceGeomComposite";
import { LayerFunc } from "src/managers/LayerFunc";
import { ApplicationStateContext } from "src/states/ApplicationState";
import { MapitUtils } from "src/managers/MapitUtils";
import { AddressInterface } from "src/managers/AddressInterface";
import { VectorLayer } from "src/managers/VectorLayerFunc";
import { MapPinPopup } from "./MapPinPopup";
import { DefaultExtractInformationResponseProperties, FeatureLayer } from "src/managers/WmsLayerFunc";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import { GlassButton } from "./GlassButtons";
import { GeojsonWFS } from "src/managers/WFSGeojsonLayerFunc";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { DC } from "src/dataCollector/DataCollector";
import { MIBIcon } from "src/componentsUtils/MIBIcon";


function isTranslatedPendingUpdated(value: any): value is TranslatedPendingUpdated {
  return value &&
    typeof value === "object" &&
    typeof value.key === "string" &&
    (typeof value.value === "string" || typeof value.value === "number") &&
    (value.pending === undefined || typeof value.pending === "boolean");
}

function determineStyling(value: string | number | TranslatedPendingUpdated | undefined, propertyName: string, layerInfo: LayerInfo): string {
  if (value === undefined) {
    return "";
  }

  if (isTranslatedPendingUpdated(value)) {
    const actualValue = value.value;
    return determineStyling(actualValue, propertyName, layerInfo);
  }

  if (value && typeof value === 'object') {
    return JSON.stringify(value);
  }

  const propertySpec = layerInfo.propertiesToDisplay?.find(prop => prop.name === propertyName);

  if (!propertySpec?.display) {
    return value.toString();
  }

  const display = propertySpec.display;
  const stringValue = value.toString();

  // Get the original data type from analysis result
  const columnInfo = layerInfo.analysisResult?.columns?.find(col => col.name === propertyName);
  const originalDataType = propertyName == "datasetname" ? "String" : columnInfo?.dataType;
  const mitDataType = columnInfo?.mitDataType;

  // Get the effective data type (use override if available, otherwise use mitDataType for specific types, otherwise original)
  const effectiveDataType = display.typeOverride ||
    (originalDataType === "String" && mitDataType === "Http" ? "link" :
      originalDataType === "String" && mitDataType === "ImageUrl" ? "ImageUrl" :
        originalDataType);

  // Helper function to try casting value to effective data type
  const tryCastToEffectiveType = (val: string | number, targetType: string): { success: boolean; castedValue: any } => {
    switch (targetType) {
      case "Float":
      case "Integer":
        const numericValue = typeof val === 'number' ? val : parseFloat(val.toString());
        return { success: !isNaN(numericValue), castedValue: numericValue };
      case "String":
        return { success: true, castedValue: val.toString() };
      case "ImageUrl":
      case "link":
        const value = val.toString();
        const hasHttpForImage = value.toLowerCase().includes("http");
        return { success: hasHttpForImage, castedValue: value };
      default:
        return { success: true, castedValue: val };
    }
  };

  // Try to cast the value to the effective data type
  const { success: castSuccess, castedValue } = tryCastToEffectiveType(value, effectiveDataType || "String");

  // If casting failed, return the original value as-is
  if (!castSuccess) {
    return stringValue;
  }

  // Handle formatting based on effective data type
  switch (effectiveDataType) {
    case "link":
      const { displayName } = display.linkFormat || { displayName: undefined };
      const linkDisplayName = displayName || castedValue;
      const target = ' target="_blank" rel="noopener noreferrer"';
      return `<a href="${castedValue}"${target}>${linkDisplayName}</a>`;

    case "ImageUrl":
      return castedValue;

    case "Float":
    case "Integer":
      if (display.numberFormat) {
        const { digitNumber, formatSuffix, scaleFactor, thousandSeparator } = display.numberFormat;

        // Apply scale factor first
        let scaledValue = castedValue;
        let scaleSuffix = "";

        switch (scaleFactor) {
          case "thousands":
            scaledValue = castedValue / 1000;
            scaleSuffix = "(K)";
            break;
          case "millions":
            scaledValue = castedValue / 1000000;
            scaleSuffix = "(M)";
            break;
          case "billions":
            scaledValue = castedValue / 1000000000;
            scaleSuffix = "(B)";
            break;
          default:
            scaleSuffix = "";
        }

        let formattedValue;
        const localLang = Localization.getLocale();

        if (!isNaN(scaledValue)) {
          if (thousandSeparator) {
            formattedValue = scaledValue.toLocaleString(localLang, {
              minimumFractionDigits: digitNumber === undefined ? 0 : digitNumber,
              maximumFractionDigits: digitNumber === undefined ? 10 : digitNumber
            });
          } else {
            formattedValue = scaledValue.toLocaleString(localLang, {
              minimumFractionDigits: digitNumber === undefined ? 0 : digitNumber,
              maximumFractionDigits: digitNumber === undefined ? 10 : digitNumber,
              useGrouping: false
            });
          }
        }

        let result = formattedValue;

        if (formatSuffix) {
          result += ` ${formatSuffix}`;
        }

        if (scaleSuffix) {
          result += ` ${scaleSuffix}`;
        }

        return result;
      }
      return castedValue.toString();

    case "String":
      if (display.stringFormat) {
        const { formatType } = display.stringFormat;
        switch (formatType) {
          case "upper":
            return castedValue.toUpperCase();
          case "lower":
            return castedValue.toLowerCase();
          case "title":
            return castedValue.replace(/\w\S*/g, (txt) =>
              txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
            );
          case "sentence":
            return castedValue.charAt(0).toUpperCase() + castedValue.slice(1).toLowerCase();
        }
      }
      return castedValue;

    default:
      return castedValue.toString();
  }
}

// MARK: CustomClsButton
function CustomCloseButton() {

  return (
    <Button style={{ marginLeft: "auto" }} size="sm" variant="outline" className="mit-map-popup-button " onClick={(e) =>
      (e.currentTarget as any).closest(".maplibregl-popup-content")?.lastChild.click()
    } title={Localization.getText("Close")}>
      <MIBIcon icon="dismiss" />
    </Button>
  )

}

function TitleLine({ title }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gridColumn: "1 / span 2" }}>
      <div style={{ height: "1px", backgroundColor: "#aaa", flex: "1" }}></div>
      <strong style={{ fontSize: "16px", flex: "1", textAlign: "center", paddingInline: "2ex" }}>
        {title}
      </strong>
      <div style={{ height: "1px", backgroundColor: "#aaa", flex: "1" }}></div>
    </div>)
}

// MARK: POINT INFO POPUP
export function PointInfoPopup(props: { lngLat: { lat: number, lng: number }, layers: FeatureLayer[] }) {
  const [dataToShow, setDataToShow] = useState<Record<string, Partial<{ data: any, title: string, extra?: { title: string, value: string }[], translator: any }>>>({})

  useEffect(() => {
    const coords = GenerateGeomUtils.convertProjection(undefined, undefined, [props.lngLat.lng, props.lngLat.lat])
    let BBox = (coords[0] - 50) + ',' + (coords[1] - 50) + ',' + (coords[0] + 50) + ',' + (coords[1] + 50)
    async function getDataToShow() {
      let x = dataToShow
      props.layers.filter((a) => a).forEach(async (SLS) => {
        let NLS = SLS.layer;
        let url = SLS.getinformationURL?.(BBox, 101, 101, 50, 50)

        if (!url) {
          return
        }

        let response;

        if (NLS.useProxy) {
          response = await MapPinPopup.getFeatureInfoViaProxy(NLS.host, url);
          console.log("Proxy response", response)
        } else {
          response = await MapPinPopup.getFeatureInfo(url);
        }

        const formatResponseProvider = (e) => DefaultExtractInformationResponseProperties(e, NLS.propertiesToDisplay || [])

        let featuresToDisplay = (formatResponseProvider ? formatResponseProvider(response) : response && response.features &&
          response.features.length > 0 && response.features[0].properties &&
          response.features.map((feature) => { return feature.properties; })).filter((properties) => { return !Utils.isEmptyObject(properties); });

        const translator = (textsTranslate) => NLS?.translationTable ? Localization.getTextSpecificTable(textsTranslate, NLS.translationTable) : textsTranslate;
        x = { ...x, [NLS.label]: { data: featuresToDisplay, extra: NLS.extraPropertiesToDisplay ?? [], title: NLS.label, translator: translator } }
        setDataToShow(x)
      });
    }
    getDataToShow()
  }
    , [])

  // MapPinPopup.formatFeatureInfoResponse([{"Lat":lngLat.lat, "Lng":lngLat.lng}], (t)=>t) as JSX.Element[])

  return (
    <>
      <div className="mit-popup-label-menu NewSecNone">
        <div></div><CustomCloseButton />
      </div>

      <div className="MapitPopupScroll">
        <div className="PopupFlex">
          {/* <div className="PopupGrid"> */}
          <div className="PopupPair"><div className="PopupKey">Lat</div><div className="PopupValue">{props.lngLat.lat}</div></div>
          <div className="PopupPair"><div className="PopupKey">Lng</div><div className="PopupValue">{props.lngLat.lng}</div></div>
          {Object.keys(dataToShow).map((a) => {
            if (!dataToShow[a].data.length) {
              return <Fragment key={a} />
            }
            return (
              <Fragment key={a}>
                <TitleLine title={dataToShow[a].translator(a)} />
                {MapPinPopup.formatFeatureInfoResponse(dataToShow[a].data, dataToShow[a].translator)}
                {MapPinPopup.formatExtraFeaturesToDisplay(dataToShow[a].extra, dataToShow[a].translator)}
              </Fragment>
            )
          })}
        </div>
        {/* </div> */}
      </div>
    </>
  )
}

export function LinePopup(props: { layerInfo, feature }) {
  const { state: mapitState } = useContext(MapitStateContext);

  let stateLayerInfo = mapitState.layers[props.layerInfo.layerId]
  if (stateLayerInfo === undefined) {
    return <></>
  }
  let stateFeature = ({ ...stateLayerInfo.geoJson.features?.[props.feature.id], id: props.feature.id });

  return <AnyLinePopup layerInfo={stateLayerInfo} feature={stateFeature || {}} />
}

// MARK: Line Popup
export function AnyLinePopup(props: { layerInfo, feature }) {
  const { state: mapitState } = useContext(MapitStateContext);
  const [editable, setEditable] = useState(false);

  return (<>
    <div className="mit-popup-label-menu NewSecNone">
      <ProtectedFeature feature={Feature.EditDataLayer} contentsIfNoAccess={<></>} >
        <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={() => { ActionTracker.track("MapPopup", "LinePopupAction", "EditDataLayer"); setEditable((a) => !a) }} title={Localization.getText("Edit")} >
          <MIBIcon icon="edit" />
        </Button>
      </ProtectedFeature>
      <CustomCloseButton />
    </div>
    <div className="MapitPopupScroll">
      {
        editable ? <LabelEditorConstructor feature={props.feature} labels={props.feature.properties} mapInfo={mapitState.mapInfo} layerInfo={props.layerInfo} /> :
          <LabelConstructorVerbatim labels={props.feature.properties} mapInfo={mapitState.mapInfo} layerInfo={props.layerInfo} />
      }
    </div>
  </>
  )
}



export function VectorLayerPopup(props: { vectorFeatures: any[] }) {
  const { state: mapitState } = useContext(MapitStateContext);

  function VectorLayerFrom(sourceId: string) {
    return Object.values(mapitState.featureLayers).find((a) => ("getSourceID" in a && typeof a.getSourceID == "function" && a.getSourceID?.() === sourceId))
  }

  const toDisplay = props.vectorFeatures.map((a, idx) => {
    const vectorLayer = VectorLayerFrom(a.layer.source)
    if (!(vectorLayer && (vectorLayer instanceof VectorLayer || vectorLayer instanceof GeojsonWFS))) {
      return <Fragment key={idx} />
    }
    const showAllProperties = !(vectorLayer.vectorHandle.propertiesToDisplay)
    let fakeLayerInfo = {
      propertiesToDisplay: (vectorLayer.vectorHandle.propertiesToDisplay || []).map((a) => ({ name: a }))
    }

    if (vectorLayer.vectorHandle.translationTable) {
      return (
        <Fragment key={idx} >
          <TitleLine title={(vectorLayer.vectorHandle.translationTable && Localization.getTextSpecificTable(vectorLayer.vectorHandle.label, vectorLayer.vectorHandle.translationTable)) || vectorLayer.vectorHandle.label} />
          <LabelConstructorTranslatedWithTable labels={a.properties} mapInfo={mapitState.mapInfo} layerInfo={fakeLayerInfo as any} showAll={showAllProperties} transTable={vectorLayer.vectorHandle.translationTable} />
          {MapPinPopup.formatExtraFeaturesToDisplay(vectorLayer.layer.extraPropertiesToDisplay, (t) => vectorLayer.vectorHandle.translationTable && Localization.getTextSpecificTable(t, vectorLayer.vectorHandle.translationTable) || t)}
        </Fragment>
      )
    }
    return (
      <Fragment key={idx} >
        <TitleLine title={(vectorLayer.vectorHandle.translationTable && Localization.getTextSpecificTable(vectorLayer.vectorHandle.label, vectorLayer.vectorHandle.translationTable)) || vectorLayer.vectorHandle.label} />
        <LabelConstructorVerbatim labels={a.properties} mapInfo={mapitState.mapInfo} layerInfo={fakeLayerInfo as any} showAll={showAllProperties} />
      </Fragment>
    )
  })

  return (
    <><div className="mit-popup-label-menu NewSecNone">
      <div></div><CustomCloseButton />
    </div>
      <div className="MapitPopupScroll">{toDisplay}</div>
    </>
  )
}

export function AreaPopup(props: { layerInfo, feature }) {
  const { state: mapitState } = useContext(MapitStateContext);

  let stateLayerInfo = mapitState.layers[props.layerInfo.layerId]
  if (stateLayerInfo === undefined) {
    return <></>
  }
  let stateFeature = window.structuredClone({ ...stateLayerInfo.geoJson.features?.[props.feature.id], id: props.feature.id });

  return <AnyAreaPopup layerInfo={stateLayerInfo} feature={{ ...stateFeature, source: props.feature.source }} />
}

// MARK: Area Popup
export function AnyAreaPopup(props: { layerInfo, feature }) {
  const { state: mapitState } = useContext(MapitStateContext);
  const { hasAccessToFeature } = useContext(ApplicationStateContext);
  const [editable, setEditable] = useState(false);
  const { dispatch: windowDispatch } = useWindowContext();
  // const type = props.feature.geometry.type
  // const {dispatch: projDispatch} = useContext(ProjectContext);


  let funcAreaLabel = () => ReferenceGeomComposite.getLabelHeader(props.layerInfo.type, props.feature, mapitState.mapInfo.popUpLayout);

  function SpatialSelectionToNewLayers(spatialSelection: SpatialReturnType) {
    let layersToAdd = Object.keys(spatialSelection).map((layerKey) => {
      let layer: LayerInfo = mapitState.layers[layerKey]
      if (!layer) {
        return
      }

      let IdsToInclude = spatialSelection[layerKey]

      const featuresToInclude = IdsToInclude.map((id) => layer.geoJson.features[id])
      const newLayerInfo = LayerFunc.createLayerInfoFromGeoJsonFeatureList(featuresToInclude, layer.datasetname)
      return newLayerInfo
    })
    let layersToRename = layersToAdd.filter(a => a)
    if (layersToRename.length) {
      windowDispatch(openWindow(WindowId.NameOnCreation, { layers: layersToAdd }))
    }
  }

  function SpatialSelectionPolygon(_e) {
    let handle = Object.keys(mapitState.layers).map((a) => mapitState.layers[a].handle).find((a) => a.sourceId === props.feature.source)
    let feature = handle?.source.data.features[props.feature.id]
    if (feature) {
      let returnGeometries = GenerateGeom.polygonsInsidePolygons(mapitState, feature)
      if (returnGeometries.length) {
        let layersToAdd = returnGeometries.map((a) => {
          return LayerFunc.createLayerInfoFromGeoJsonFeatureList(a.features, a.name)
        })
        windowDispatch(openWindow(WindowId.NameOnCreation, { layers: layersToAdd }))
      }
    }

  }

  function SpatialSelection(_e: React.MouseEvent<HTMLButtonElement, MouseEvent>, inverse: boolean = false) {
    let handle = Object.keys(mapitState.layers).map((a) => mapitState.layers[a].handle).find((a) => a.sourceId === props.feature.source)
    let feature = handle?.source.data.features[props.feature.id]
    if (feature) {
      let spatialReturn = GenerateGeom.spatialSelectionGeoJSON(mapitState, feature.geometry, inverse);
      SpatialSelectionToNewLayers(spatialReturn);
    }
  }

  function SpatialCheck() {
    if (!hasAccessToFeature(Feature.SpatialExportGeoJsonPreview)) {
      return ""
    }
    let handle = Object.keys(mapitState.layers).map((a) => mapitState.layers[a].handle).find((a) => a.sourceId === props.feature.source)
    let feature = handle?.source.data.features[props.feature.id]
    if (feature) {
      let spatialReturn = GenerateGeom.spatialSelectionGeoJSON(mapitState, feature.geometry);
      return Object.keys(spatialReturn).reduce((b, a) => { return spatialReturn[a].length + b }, 0)
    }
    return "?"
  }


  let child: JSX.Element | undefined = undefined;
  if (MapitUtils.isAreaLayer(props.layerInfo.type)) {
    child = <><div>{funcAreaLabel()}</div><div dangerouslySetInnerHTML={{ __html: props.feature.labelHTML }}></div></>
  } else {
    child = editable ? <LabelEditorConstructor feature={props.feature} labels={props.feature.properties} mapInfo={mapitState.mapInfo} layerInfo={props.layerInfo} />
      :
      <LabelConstructorVerbatim labels={props.feature.properties} mapInfo={mapitState.mapInfo} layerInfo={props.layerInfo} />
  }

  return (<>
    <div className="mit-popup-label-menu NewSecNone">
      {props.feature.properties?.SFEnummer ?
        <ProtectedFeature feature={Feature.PropertyInfo} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button" onClick={async () => {
            windowDispatch(openWindow(WindowId.PropertyInformationDialog, { bfeNr: props.feature.properties?.SFEnummer }))
          }} title={Localization.getText("Lookup property information")}>
            <MIBIcon icon="propertyInfo" />
          </Button>
        </ProtectedFeature>
        : <></>}
      <ProtectedFeature feature={Feature.SpatialExportGeoJSON} contentsIfNoAccess={<></>} >
        <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={(e) => { ActionTracker.track("MapPopup", "AreaPopupAction", "SpatialExportGeoJSON"); SpatialSelection(e); }} title={Localization.getText("Spatial Export")}>
          {SpatialCheck()} <MIBIcon icon="spatialFilter" />
        </Button>
      </ProtectedFeature>
      <ProtectedFeature feature={Feature.SpatialExportGeoJSONInverse} contentsIfNoAccess={<></>} >
        <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={(e) => { ActionTracker.track("MapPopup", "AreaPopupAction", "SpatialExportGeoJSONInverse"); SpatialSelection(e, true); }} title={Localization.getText("Spatial Export Inverse")}>
          <MIBIcon icon="spatialFilterInverse" />
        </Button>
      </ProtectedFeature>
      <ProtectedFeature feature={Feature.IntersectingPolygons} contentsIfNoAccess={<></>} >
        <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={(e) => { ActionTracker.track("MapPopup", "AreaPopupAction", "SpatialExportPolygon"); SpatialSelectionPolygon(e); }} title={Localization.getText("Spatial Export Polygon")} >
          <MIBIcon icon="spatialFilterPolygon" />
        </Button>
      </ProtectedFeature>
      {MapitUtils.isAreaLayer(props.layerInfo.type) ? <></> :
        <ProtectedFeature feature={Feature.EditDataLayer} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={() => { ActionTracker.track("MapPopup", "AreaPopupAction", "EditDataLayer"); setEditable((a) => !a) }} title={Localization.getText("Edit")} >
            <MIBIcon icon="edit" />
          </Button>
        </ProtectedFeature>
      }
      <CustomCloseButton />
    </div>
    <div className="MapitPopupScroll">
      {child}
    </div>
  </>)
}

export function PointPopup(props: { layerInfo: LayerInfo, feature: any, lat: number, lng: number }) {
  const { state: mapitState } = useContext(MapitStateContext);
  let UsingThemeNewsec = document.body.classList.contains("Theme-Newsec")

  let stateLayerInfo = mapitState.layers[props.layerInfo.layerId] || props.layerInfo
  if (stateLayerInfo === undefined) {
    return <></>
  }
  let stateFeature = ({ ...stateLayerInfo.geoJson?.features?.[props.feature.id], id: props.feature.id });

  if (UsingThemeNewsec) {
    return <NewSecPopup {...props} layerInfo={stateLayerInfo} feature={stateFeature || {}} />
  }
  return <ViamapPopup {...props} layerInfo={stateLayerInfo} feature={stateFeature || {}} />

}


export function NewSecPopup(props: { layerInfo: LayerInfo, feature: any, lat: number, lng: number, }) {
  const { state: mapitState } = useContext(MapitStateContext);
  const { dispatch: windowDispatch } = useWindowContext();
  const { state: projState, dispatch: projDispatch } = useContext(ProjectContext);
  const [hasBeenAdded, setHasBeenAdded] = useState<boolean>(false)
  const { dispatch: appMessageDispatch } = useContext(AppMessagesContext)
  const [matData, setMatData] = useState<{ mat?: string, ejerlav?: string, err?: boolean }>({});
  const [data, setData] = useState({
    "Ground Area": { key: "Total property area", value: "", pending: true },
    "Transaction value": { key: "Last Transaction Amt", value: "", pending: true },
    "Transaction date": { key: "Last Transaction Date", value: "", pending: true },
    "Owner": { key: "Owner", value: "", pending: true },
  })

  useEffect(() => {
    const signal = new AbortController()
    if ((props.layerInfo.propertiesToDisplay || []).length === 0) {
      CollectData(signal)
    }
    return () => signal.abort()
  }, []);

  async function CollectData(_signal: AbortController) {

    function setInfo<T extends keyof typeof data>(key: T, value: any) {
      setData((a) => {
        let x = { ...a }
        x[key] = { key: x[key].key, value: value, pending: false }
        return x;
      })
    }
    let formatter = new Intl.NumberFormat("da-dk")


    let latLng = new MitLatLng(props.lat, props.lng)
    let utmCoords = GenerateGeom.convertFromLatLngToUTM(latLng);
    let matData = (await PropertyInfoInterface.getMatrikelData(utmCoords))?.features[0]?.properties;
    if (matData.matrikelnummer) setMatData({ mat: matData.matrikelnummer, ejerlav: matData.ejerlavskode })
    if (!matData.matrikelnummer) {
      setMatData({ err: true })
      appMessageDispatch(actionSetInfoMessage(Localization.getText("No property for location")))
    }
    let ejdData = matData && await DC.DLL.EjendomData(matData.bfenummer)
    setInfo("Ground Area", ejdData?.totalAreal)
    let ejerer = ejdData?.ejere.map((a) => a.navn + (("CVRNummer" in a) ? ` '${a.CVRNummer}'` : "")).join("\n")
    setInfo("Owner", ejerer)
    let HanDataAll = ejdData && (await DC.DLL.EjendomHandel(ejdData.sag.BFEnr))?.handel?.features
    let HanData = HanDataAll?.toSorted((a, b) => a.properties.virkningFra > b.properties.virkningFra ? -1 : 1)?.[0]?.properties

    let lastTransactionDato = HanData?.virkningFra || "";
    let lastTransactionSum = HanData?.samletKoebesum || 0;
    setInfo("Transaction value", lastTransactionSum !== 0 ? formatter.format(lastTransactionSum) : Localization.getText("Not available"))
    setInfo("Transaction date", lastTransactionDato.split("T")[0] || lastTransactionDato)

  }

  if (matData.err) {
    return
  }

  return (
    <>
      <div className="mit-abs-popup-label-menu" >
        <CustomCloseButton />
      </div>
      <h4>{props.layerInfo.datasetname}</h4>
      {matData.mat && <h4>{matData.mat} / {matData.ejerlav}</h4>}
      <div className="MapitPopupScroll">
        {
          props.layerInfo.propertiesToDisplay?.length ?
            <LabelConstructorVerbatim labels={props.feature.properties} mapInfo={mapitState.mapInfo} layerInfo={props.layerInfo} />
            :
            <LabelConstructorTranslated labels={data} mapInfo={mapitState.mapInfo} layerInfo={props.layerInfo} showAll={true} />
        }
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "7px", marginTop: "7px" }}>
        <ProtectedFeature feature={Feature.PropertyInfo} contentsIfNoAccess={<span style={{ display: "none" }}></span>} >
          <GlassButton onClick={async () => {
            let bfeData = await DC.WFS.Utils.bfeUnderPoint([props.lng, props.lat]);
            windowDispatch(openWindow(WindowId.PropertyInformationDialog, { bfeNr: bfeData?.sfeNr, ejerlavskode: bfeData?.ejerl, matrnr: bfeData?.matnr }))
          }} >{Localization.getText("Lookup property information")}</GlassButton>
        </ProtectedFeature>
        <ProtectedFeature feature={Feature.AddToProjectUnassignedList} contentsIfNoAccess={<span style={{ display: "none" }}></span>} >
          {projState.isLoaded ? <>
            <GlassButton onClick={async () => {
              if (hasBeenAdded) {
                return
              }
              if (projState.isEmpty) {
                appMessageDispatch(actionSetInfoMessage(Localization.getText("Please visit the Project tabs, an action is needed!")))
                return
              }
              let resp = await AddressInterface.reverseGeocodeFull(props.lat, props.lng);
              let addrData = await resp.json();
              let jordstykke = await (await fetch(addrData.jordstykke.href)).json()
              let fullAddress = addrData.adressebetegnelse;
              let tmp = fullAddress.split(',');
              let streetAndNumber = tmp[0] || fullAddress;
              let zipAndCity = addrData.postnummer.nr + " " + addrData.postnummer.navn;

              projDispatch(handleToBeAdded([{ bfeNr: jordstykke.bfenummer, name: streetAndNumber + ", " + zipAndCity, latLng: [props.lat, props.lng], tags: [] }]))
              setHasBeenAdded(true)
              return
            }} >{hasBeenAdded ? Localization.getText("Already added") : Localization.getText("Add to Project")}
            </GlassButton>

          </> : null}

        </ProtectedFeature>
      </div>
    </>
  )
}

// MARK: Viamap Point Popup
export function ViamapPopup(props: { layerInfo: LayerInfo, feature: any, lat: number, lng: number }) {
  const [editable, setEditable] = useState(false);
  const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
  const { dispatch: windowDispatch } = useWindowContext();
  const { dispatch: appMessageDispatch } = useContext(AppMessagesContext)
  const { state: catchmentState, dispatch: catchmentStateDispatch } = useContext(CatchmentStateContext);

  async function visSamletFastEjendom(latLng: MitLatLng, name: string): Promise<void> {
    try {
      let utmCoords = GenerateGeom.convertFromLatLngToUTM(latLng);
      appMessageDispatch(actionSetInfoMessage(Localization.getText("Retrieving cadaster data")));
      let matrikelData = await PropertyInfoInterface.getMatrikelData(utmCoords)
        .then(result => {
          let props = result.features[0].properties;
          return {
            sfeEjdNr: props.samletfastejendomlokalid,
          };
        }).catch(() => {
          appMessageDispatch(actionSetErrorMessage(Localization.getText("Cannot display cadaster information for this point")));
          return undefined;
        });
      if (matrikelData && matrikelData.sfeEjdNr) {
        appMessageDispatch(actionSetInfoMessage(Localization.getText("Retrieving geometry data")));
        let geoJson = await PropertyInfoInterface.getGeojsonOfSFE(matrikelData.sfeEjdNr)
          .then(result => result)
          .catch(() => {
            appMessageDispatch(actionSetErrorMessage(Localization.getText("No geometry data for this property")));
            return undefined;
          });
        geoJson.type = "FeatureCollection"
        Persistence.createLayersFromGeoJSON(name + " (sfe)", geoJson, new Date(), (li: LayerInfo) => { mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: { ...li, propertiesToDisplay: [{ name: "SFEnummer" }] } } }) } /*, undefined, 0.25*/);
        appMessageDispatch(actionClearInfoMessage());
      } else {
        appMessageDispatch(actionSetErrorMessage(Localization.getText("No cadaster data for this point")));
      }
    } catch (error) {
      console.error(error);
      appMessageDispatch(actionSetErrorMessage(Localization.getText("Cannot display cadaster information for this point")));
    }
  }

  async function findVurderingsEjendom(latLng: MitLatLng): Promise<{ sfe: string, geom: any }> {
    let bfeData = await DC.WFS.Utils.bfeUnderPoint([latLng.lng, latLng.lat]);
    if (!bfeData) {
      throw new Error("No property found under point");
    }
    let ejendommeIVurdering = await DC.DF_GQL.VURD.EjendommeVurderetUnderBFE(bfeData.sfeNr + "");
    if (!ejendommeIVurdering || ejendommeIVurdering.length === 0) {
      ejendommeIVurdering = [bfeData.sfeNr + ""]
    }
    let samletGeometry = await DC.WFS.getFeatureQuery("Matriklen2", "Jordstykke_Gaeldende",
      `samletFastEjendomLokalId in (${ejendommeIVurdering.map((a) => `'${a}'`).join(",")})` // eg. ('351352','351234','412454')
    )
    console.log("Samlet geometry for vurderingsejendom:", samletGeometry)
    return { sfe: bfeData.sfeNr, geom: samletGeometry };
  }

  async function visVurderingsEjendom(latLng: MitLatLng, name: string): Promise<void> {
    try {
      let { geom } = await findVurderingsEjendom(latLng);
      Persistence.createLayersFromGeoJSON(name + " (vurd)", geom, new Date(), (li: LayerInfo) => { mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: { ...li } } }) } /*, undefined, 0.25*/);
    } catch (error) {
      console.error(error);
      appMessageDispatch(actionSetErrorMessage(Localization.getText("Cannot display property estimate information for this point")));
    }
  }

  return (
    <>
      <div className="mit-popup-label-menu">
        <ProtectedFeature feature={Feature.PropertyInfo} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button" onClick={async () => {
            let bfeData = await DC.WFS.Utils.bfeUnderPoint([props.lng, props.lat]);
            windowDispatch(openWindow(WindowId.PropertyInformationDialog, { bfeNr: !props.feature.properties?.SFEnummer ? bfeData?.sfeNr : props.feature.properties?.SFEnummer, ejerlavskode: bfeData?.ejerl, matrnr: bfeData?.matnr }))

          }} title={Localization.getText("Lookup property information")}>
            <MIBIcon icon="propertyInfo" />
          </Button>
        </ProtectedFeature>
        <ProtectedFeature feature={Feature.TravelTime} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={() => {
            CatchmentInterface.clearCache(); // clear cache as new point is selected
            windowDispatch(closeWindow(WindowId.PointDistanceTable));
            ActionTracker.track("MapPopup", "PopupAction", "TravelTime");
            catchmentStateDispatch(actionGenerateCatchmentSimple(catchmentState, mapitState, new MitLatLng(props.lat, props.lng)));
            windowDispatch(openWindow(WindowId.CatchmentSelector, { latlng: new MitLatLng(props.lat, props.lng) }));
          }} title={Localization.getText("Travel Time")}>
            <MIBIcon icon="catchment" />
          </Button>
        </ProtectedFeature>
        <ProtectedFeature feature={Feature.TravelTimeFromPoint} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={() => {
            ActionTracker.track("MapPopup", "PopupAction", "TravelTimeFromPoint");
            windowDispatch(openWindow(WindowId.PointDistanceTable, { dialogMode: "PointToPoint", latlng: new MitLatLng(props.lat, props.lng), layerId: props.feature.layerId }))
          }} title={Localization.getText("Point Distance")}>
            <MIBIcon icon="pointDistance" />
          </Button>
        </ProtectedFeature>
        <ProtectedFeature feature={Feature.ObliquePhoto} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={() => { ActionTracker.track("MapPopup", "PopupAction", "ObliquePhoto"); windowDispatch(openWindow(WindowId.ObliqueViewer, { latlng: new MitLatLng(props.lat, props.lng) })) }} title={Localization.getText("Oblique Photo")}>
            <MIBIcon icon="obliquePhoto" />
          </Button>
        </ProtectedFeature>
        <ProtectedFeature feature={Feature.StreetView} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={() => { ActionTracker.track("MapPopup", "PopupAction", "StreetView"); windowDispatch(openWindow(WindowId.StreetView, { ...new MitLatLng(props.lat, props.lng) })) }} title={Localization.getText("Go to street view")}>
            <MIBIcon icon="streetView" />
          </Button>
        </ProtectedFeature>
        <ProtectedFeature feature={Feature.Kortviser} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={() => { throw new Error("Not Implemented") }} title={Localization.getText("Lookup in Kortviser")}>
            <MIBIcon icon="kortviser" />
          </Button>
        </ProtectedFeature>
        <ProtectedFeature feature={Feature.LifaOIS} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={() => { throw new Error("Not Implemented") }} title={Localization.getText("Lookup in Lifa OIS")}>
            <MIBIcon icon="lifaOIS" />
          </Button>
        </ProtectedFeature>
        <ProtectedFeature feature={Feature.VisSamletFastEjendom} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={() => { ActionTracker.track("MapPopup", "PopupAction", "VisSamletFastEjendom"); visSamletFastEjendom(new MitLatLng(props.lat, props.lng), props.layerInfo.datasetname || "Ukendt") }} title={Localization.getText("Show total real estate on map")}>
            <MIBIcon icon="visSamletFastEjendom" />
          </Button>
        </ProtectedFeature>
        <ProtectedFeature feature={Feature.VisVurderingsejendom} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={() => { ActionTracker.track("MapPopup", "PopupAction", "VisVurderingsEjendom"); visVurderingsEjendom(new MitLatLng(props.lat, props.lng), props.layerInfo.datasetname || "Ukendt") }} title={Localization.getText("Show real estate estimate on map")}>
            <MIBIcon icon="visVurderingsEjendom" />
          </Button>
        </ProtectedFeature>
        <ProtectedFeature feature={Feature.LinkHistoriskeKort} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={() => { ActionTracker.track("MapPopup", "PopupAction", "Historiskekort"); window.open(`https://historiskekort.dk/?geometri=POINT(${props.lng}+${props.lat})&limit=50&offset=0&sort=titel&direction=asc`) }} title={Localization.getText("Historiskekort")}>
            <MIBIcon icon="historiskekort" />
          </Button>
        </ProtectedFeature>
        <ProtectedFeature feature={Feature.EditDataLayer} contentsIfNoAccess={<></>} >
          <Button size="sm" variant="outline" className="mit-map-popup-button " onClick={() => { ActionTracker.track("MapPopup", "PopupAction", "EditDataLayer"); setEditable((a) => !a) }} title={Localization.getText("Edit")} >
            <MIBIcon icon="edit" />
          </Button>
        </ProtectedFeature>
        <CustomCloseButton />
      </div>
      <div className="MapitPopupScroll">
        {
          editable ? <LabelEditorConstructor feature={props.feature} labels={props.feature.properties} mapInfo={mapitState.mapInfo} layerInfo={props.layerInfo} /> :
            <LabelConstructorVerbatim labels={props.feature.properties} mapInfo={mapitState.mapInfo} layerInfo={props.layerInfo} />
        }
      </div>
    </>
  )
}

type TranslatedPendingUpdated = {
  key: string,
  value: string | number,
  pending?: boolean
}

type LabelConstructorProps = {
  labels: { [key: string]: string | number | TranslatedPendingUpdated },
  mapInfo: MapInfo,
  layerInfo: LayerInfo,
  showAll?: boolean
}


export function LabelConstructorVerbatim({ labels, mapInfo, layerInfo, showAll = false }: LabelConstructorProps) {
  const viewedLabels = includeDatasetName(labels, layerInfo.datasetname)
  const id = useId();
  let listToDisplay = layerInfo.propertiesToDisplay?.map((p) => p.name) || [];
  if (showAll) {
    listToDisplay = Object.keys(viewedLabels)
  }
  const lKeys = listToDisplay
  return (<PopupGrid >{lKeys.map((lKey) => {
    const originalValue = viewedLabels[lKey];
    let styledValue;
    const valKey = (() => {
      const propertySpec = layerInfo.propertiesToDisplay?.find(prop => prop.name === lKey);

      if (lKey === "datasetname") {
        return propertySpec?.display?.aliasName || Localization.getText("Dataset name");
      }

      return propertySpec?.display?.aliasName || lKey;
    })();

    if (isTranslatedPendingUpdated(originalValue)) {
      styledValue = determineStyling(originalValue.value, lKey, layerInfo);
    } else if (originalValue && typeof originalValue === 'object') {
      styledValue = JSON.stringify(originalValue);
    } else if (originalValue !== undefined && originalValue !== null) {
      styledValue = determineStyling(originalValue, lKey, layerInfo);
    } else {
      styledValue = "";
    }

    // Images or Link
    if (styledValue && (styledValue.toLowerCase().startsWith("http") || styledValue.toLowerCase().startsWith("blob:http"))) {
      return <ImageLinkFallback key={valKey} lKey={lKey} value={styledValue} mapInfo={mapInfo} Pid={id} layerInfo={layerInfo} />
    }
    // HTML
    if (styledValue && styledValue.includes('<') && styledValue.includes('>')) {
      return <div key={valKey} className="PopupPair" dangerouslySetInnerHTML={{ __html: styledValue }}></div>
    }
    return <div className="PopupPair" key={valKey}><div className="PopupKey">{valKey}</div><div className="PopupValue">{styledValue}</div></div>
  })}</PopupGrid>);
}

export function LabelConstructorTranslatedWithTable({ labels, mapInfo, layerInfo, showAll = false, transTable }: LabelConstructorProps & { transTable: SimpleTranslationTable }) {
  let translatedLabels = {}
  let propertiesToDisplay = layerInfo.propertiesToDisplay?.map((a) => a.name)
  let keys = Object.keys(labels).filter((a) => showAll || propertiesToDisplay?.includes(a));

  keys.forEach((key) => {
    const value = labels[key];
    const translatedKey = Localization.getTextSpecificTable(key, transTable);
    const styledValue = determineStyling(value as string, key, layerInfo);
    translatedLabels[translatedKey] = styledValue as any;
  })

  return LabelConstructorVerbatim({ labels: translatedLabels, mapInfo, layerInfo, showAll: true })
}

export function LabelConstructorTranslated({ labels, mapInfo, layerInfo }: LabelConstructorProps) {
  let translatedLabels = {}
  let keys = Object.keys(labels)
  keys.forEach((key) => {
    let value = labels[key]
    if (typeof value === "object") {
      const translatedKey = Localization.getText(value.key);
      const styledValue = determineStyling(value.value, key, layerInfo);
      translatedLabels[translatedKey] = styledValue;
    } else {
      const styledValue = determineStyling(value, key, layerInfo);
      translatedLabels[key] = styledValue;
    }
  })

  return LabelConstructorVerbatim({ labels: translatedLabels, mapInfo, layerInfo, showAll: true })
}
export function PopupGrid({ children }) {
  return (
    <>
      <div className="PopupFlex" >
        {/* {layout === "Left" ? <div className="popupImgContainer" id={Pid} /> : null}
        <div className="PopupGrid">
        {layout === "Center" ? <div className="popupImgContainer" id={Pid} style={{gridColumn: "1 / span 2"}} /> : null} */}
        {children}
        {/* </div>
        {layout === "Right" ? <div className="popupImgContainer" id={Pid} /> : null} */}
      </div>
    </>
  )
}

export function ImageLinkFallback(props: { lKey, value, mapInfo, Pid, layerInfo?}) {
  const [failed, setFailed] = useState(false);
  const [_loaded, setLoaded] = useState(false); //TODO: use loaded to show spinner or similar while loading

  const propertySpec = props.layerInfo?.propertiesToDisplay?.find(prop => prop.name === props.lKey);
  const linkFormat = propertySpec?.display?.linkFormat;
  const formatType = linkFormat?.formatType || "default";

  const displayName = linkFormat?.displayName || props.value;
  const target = '_blank';
  const rel = 'noopener noreferrer';

  // If formatType is "url", always show as text link
  if (formatType === "url") {
    return (
      <div className="PopupPair">
        <div style={{ gridColumn: "1" }}>{props.lKey}</div>
        <a
          key={props.value}
          style={{ paddingLeft: "10px", gridColumn: "2", wordBreak: "break-all", textAlign: "right" }}
          target={target}
          rel={rel}
          href={props.value}
        >
          {displayName}
        </a>
      </div>
    );
  }

  const getImageSize = (size: "Small" | "Medium" | "Large") => {
    const baseSize = 350;
    const scaleFactors = {
      "Small": 0.75,
      "Medium": 1.0,
      "Large": 1.25
    };

    const scaledSize = baseSize * scaleFactors[size];
    return {
      maxWidth: `${scaledSize}px`,
      maxHeight: `${scaledSize}px`
    };
  };

  const imageSize = formatType === "image"
    ? (linkFormat?.imageSize || props.mapInfo.popUpPictureSize || "Medium")
    : (props.mapInfo.popUpPictureSize || "Medium");

  const sizeStyle = getImageSize(imageSize);

  if (failed) {
    const fallbackText = formatType === "image" ? displayName : props.value;

    return (
      <div className="PopupPair">
        <div style={{ gridColumn: "1" }}>{props.lKey}</div>
        <a
          key={props.value}
          style={{ paddingLeft: "10px", gridColumn: "2", wordBreak: "break-all", textAlign: "right" }}
          target={target}
          rel={rel}
          href={props.value}
        >
          {fallbackText}
        </a>
      </div>
    );
  }

  // Try to load as image
  return (
    <div className={"mit-label-image-border"}>
      <img
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        style={{
          ...sizeStyle,
          objectFit: "contain",
          borderRadius: "4px"
        }}

        src={props.value}
        onDragStart={() => false}
        onClick={() => {
          if (props.value) window.open(props.value, target);
        }}
        alt={props.value}
        title={props.value}
      />
    </div>
  );
}

// MARK: In Popup Editor
function LabelEditorConstructor({ feature, layerInfo }: LabelConstructorProps & { feature: any }) {
  const { dispatch } = useContext(MapitStateContext);
  const [editProp, setEditProp] = useState({ ...feature.properties });
  const [statLabel, setStatLabel] = useState([...(layerInfo.propertiesInGeoJson || [])]);
  const [editLabel, setEditLabel] = useState([...(layerInfo.propertiesInGeoJson || [])]);
  const [nextField, setNextField] = useState("")


  function handleChangeProp(a, newValue) {
    setEditProp((pre) => ({
      ...pre,
      [a]: newValue
    }))
  }

  function handleChangeLabel(a, newValue) {
    setEditLabel((pre) =>
      pre.map((oldValue, idx) => a == idx ? newValue : oldValue)
    )
  }

  function updateLayer() {
    if (new Set(editLabel.filter((a) => a)).size != editLabel.filter((a) => a).length) {
      return
    }

    let nextProp = {}
    let nGjson = layerInfo.geoJson.features.map((ftl, ftlId) => {
      let newProp = {}
      statLabel.forEach((a, idx) => {
        if (editLabel[idx] && feature.id == ftlId) {
          newProp = { ...newProp, [editLabel[idx]]: editProp[a] }
        } else if (editLabel[idx]) {
          newProp = { ...newProp, [editLabel[idx]]: ftl.properties[a] }
        }
      })
      if (feature.id == ftlId) {
        nextProp = newProp
      }
      return { ...ftl, id: ftlId, properties: newProp }
    })

    let convName = { "datasetname": "datasetname" }
    statLabel.forEach((a, idx) => {
      if (editLabel[idx])
        convName = {
          ...convName,
          [a]: editLabel[idx]
        }
    })


    let nextByProperty = {}
    Object.keys(layerInfo.styling).forEach((a) => {
      if (a.includes("ByProperty")) {
        if (Array.isArray(layerInfo.styling[a])) {
          nextByProperty = { ...nextByProperty, [a]: layerInfo.styling[a].filter((b) => (convName[b])) }
        } else {
          nextByProperty = { ...nextByProperty, [a]: convName[layerInfo.styling[a]] }
        }
      }
    })
    setEditProp(() => ({ ...nextProp }))
    setStatLabel(() => editLabel.filter((a) => a))
    setEditLabel((a) => a.filter((a) => a))
    dispatch(actionUpdateDataLayer(
      layerInfo.layerId,
      {
        ...layerInfo,
        geoJson: { ...layerInfo.geoJson, features: nGjson },
        propertiesInGeoJson: editLabel.filter((a) => a),
        styling: { ...layerInfo.styling, ...nextByProperty },
        propertiesToDisplay: layerInfo.propertiesToDisplay?.flatMap((a) => (convName[a.name] && { name: convName[a.name] } || []))
      }
    )
    )
  }

  function handleNewField() {
    if (editLabel.includes(nextField)) {
      return
    }

    setEditProp((pre) => ({ ...pre, [nextField]: "" }))
    setEditLabel((pre) => ([...pre, nextField]))
    setStatLabel((pre) => ([...pre, nextField]))
    setNextField("")
    setTimeout(function () {
      let x = (document.getElementsByClassName("PropsEditors") as any) as HTMLInputElement[]
      x[x.length - 1]?.focus()
    }, 200)
  }

  return (<div className="DataEditor">
    {
      statLabel?.map((a, idx) => {
        return (
          <Fragment key={a}>
            <div><input onChange={(e) => handleChangeLabel(idx, e.target.value)} value={editLabel[idx] ?? ""}
              data-invalid={editLabel[idx] && checkIfDuplicates(editLabel, editLabel[idx], idx)}
              data-placeholdervariant="warning"
              placeholder={Localization.getText("Will be deleted")}
            /></div>
            <div><textarea className="PropsEditors" onChange={(e) => handleChangeProp(a, e.target.value)} style={{ textAlign: "right", width: "100%" }} value={editProp[a] ?? ""} /></div>
          </Fragment>
        )
      })
    }
    <div style={{ display: "flex" }}>
      <input placeholder={Localization.getText("Add field")} className="newField" onChange={(e) => setNextField(e.target.value)} onKeyDown={
        (e) => {
          if ((e.code).toLowerCase() == "enter") handleNewField()
        }
      }
        onBlur={() => {
          if (nextField) handleNewField()
        }}
        value={nextField}
        data-invalid={nextField && checkIfDuplicates(editLabel, nextField, -1)}
      ></input>
      {(CompareArr(Object.keys(feature.properties), editLabel) || (CompareArr(Object.values(editProp), Object.values(feature.properties)))) ? <div className="UnsavedIndicator" style={{ marginLeft: "auto" }}>{Localization.getText("Unsaved Changes")}</div> : <div style={{ marginLeft: "auto" }}></div>}
      <button onClick={() => {
        updateLayer()
      }}>{Localization.getText("Save")}</button>
    </div>
  </div>)

}

function checkIfDuplicates(propeties: any[], key, idx) {
  return propeties.some((a, id) => {
    return (a == key && idx != id)
  })
}

function includeDatasetName<T extends object>(properties: T, datasetname?: string) {
  if (datasetname) {
    return { ...properties, datasetname: datasetname }
  }
  return properties

}


function CompareArr(arr1: any[], arr2: any[]): boolean {
  return arr1.some((element, idx) => {
    return (arr2[idx] != element)
  });
}